import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../../config";
import { toApiDate } from "../customerPerformanceHelpers";
import {
  getCell,
  getTotalJobs,
  getIssuePercent,
} from "./tieredCustomerPerformanceHelpers";
import "./TieredCustomerPerformance.css";

// "Tiered Customer Performance" widget — QTD/YTD counts of operational change-job
// events (Quality, Shortage, and the Uncovered Labor / Material Stockout reschedule
// reasons) for orders whose customer had a priority number or was "New" at order
// creation. The backend does all the eligibility/bucketing work; this just renders.
//
// dataScope === "self" scopes to the logged-in salesperson (order.SalesPersonId).
// `slug` ties each row to its drill-down route (/customer-performance/:reason).
const ROWS = [
  { key: "uncoveredLabor", label: "Uncovered Labor", slug: "uncovered-labor" },
  { key: "uncoveredMaterialStockout", label: "Uncovered Material Stockout", slug: "uncovered-material-stockout" },
  { key: "qualities", label: "Qualities", slug: "qualities" },
  { key: "shortages", label: "Shortages", slug: "shortages" },
];

const TieredCustomerPerformance = ({ auth: { auth }, dataScope = "all", userId }) => {
  const [data, setData] = useState(null);
  // Tracks which individual cell (e.g. "qualities:qtd") was last clicked so we
  // can highlight it and show the user exactly what they selected.
  const [selectedCell, setSelectedCell] = useState(null);
  const navigate = useNavigate();

  const token = auth && auth[0] ? auth[0].token : null;
  const scopedSalesPersonId = dataScope === "self" ? userId : null;

  useEffect(() => {
    if (!token) return;
    const params = {};
    if (scopedSalesPersonId) params.salesPersonId = scopedSalesPersonId;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/TieredCustomerPerformance`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setData(rows[0] || null);
      })
      .catch((err) => {
        console.warn("Could not load Tiered Customer Performance:", err.message);
      });
  }, [token, scopedSalesPersonId]);

  const cell = (key, period) => getCell(data, key, period);

  // Drill down to the listing, forwarding the row's reason + date window from
  // the widget response so GetOrderChangeHistory filters on the same values.
  // The from-date depends on which part of the row was clicked: the label and
  // the QTD cell use qtdFromDate (quarter-to-date), the YTD cell uses
  // ytdFromDate (year-to-date). Both share the same toDate.
  const handleRowClick = (r, period, cellId) => {
    setSelectedCell(cellId);
    const rowData = data && data[r.key] ? data[r.key] : null;
    const params = new URLSearchParams();
    if (rowData) {
      if (rowData.reason != null) params.set("reason", rowData.reason);
      const fromDate =
        period === "ytd"
          ? rowData.ytdFromDate
          : period === "wtd"
          ? rowData.wtdFromDate
          : rowData.qtdFromDate;
      // Send only the date part (YYYY-MM-DD), not the full ISO datetime.
      if (fromDate) params.set("fromDate", toApiDate(fromDate));
      if (rowData.toDate) params.set("toDate", toApiDate(rowData.toDate));
    }
    const qs = params.toString();
    navigate(`/customer-performance/${r.slug}${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="tcp-widget">
      <h4 className="tcp-title">Tiered Customer Performance</h4>
      <table className="tcp-table">
        <thead>
          <tr>
            <th className="tcp-rowhead"></th>
            <th>WTD</th>
            <th>QTD</th>
            <th>YTD</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => {
            const wtdId = `${r.key}:wtd`;
            const qtdId = `${r.key}:qtd`;
            const ytdId = `${r.key}:ytd`;
            const labelId = `${r.key}:label`;
            const cellCls = (base, id) =>
              `${base} tcp-cell${selectedCell === id ? " tcp-selected" : ""}`;
            return (
              <tr key={r.key}>
                <td
                  className={cellCls("tcp-label", labelId)}
                  onClick={() => handleRowClick(r, "ytd", labelId)}
                >
                  {r.label}
                </td>
                <td
                  className={cellCls("tcp-num", wtdId)}
                  onClick={() => handleRowClick(r, "wtd", wtdId)}
                >
                  {cell(r.key, "wtd")}
                </td>
                <td
                  className={cellCls("tcp-num", qtdId)}
                  onClick={() => handleRowClick(r, "qtd", qtdId)}
                >
                  {cell(r.key, "qtd")}
                </td>
                <td
                  className={cellCls("tcp-num", ytdId)}
                  onClick={() => handleRowClick(r, "ytd", ytdId)}
                >
                  {cell(r.key, "ytd")}
                </td>
              </tr>
            );
          })}
          {/* Summary rows — read-only totals across the same WTD/QTD/YTD windows. */}
          <tr className="tcp-summary">
            <td className="tcp-label">Total Number of Jobs</td>
            <td className="tcp-num">{getTotalJobs(data, "weekJobCount")}</td>
            <td className="tcp-num">{getTotalJobs(data, "qtdJobCount")}</td>
            <td className="tcp-num">{getTotalJobs(data, "ytdJobCount")}</td>
          </tr>
          <tr className="tcp-summary">
            <td className="tcp-label">Change Jobs %</td>
            <td className="tcp-num">{getIssuePercent(data, "weekPercentJobsWithIssue")}%</td>
            <td className="tcp-num">{getIssuePercent(data, "qtdPercentJobsWithIssue")}%</td>
            <td className="tcp-num">{getIssuePercent(data, "ytdPercentJobsWithIssue")}%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(TieredCustomerPerformance);
